import axios from 'axios';

// Normalize so Vercel/dashboard paste mistakes (trailing whitespace or literal \n)
// cannot break axios with a malformed baseURL.
const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000')
  .replace(/\\n/g, '\n')
  .replace(/\\r/g, '\r')
  .replace(/[\r\n]+/g, '')
  .trim()
  .replace(/\/+$/, '');

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout for Render free tier
  headers: {
    'Content-Type': 'application/json',
  },
});

function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

function normalizeSubscriptionStatus(status: string | null | undefined): string | null {
  if (status == null) {
    return null;
  }
  const cleaned = status.trim().toLowerCase();
  return cleaned || null;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'name',
  age: 'age',
  email: 'email',
  password: 'password',
  phone: 'phone number',
  street: 'street address',
  city: 'city',
  state: 'state',
  zip: 'ZIP code',
  country: 'country',
};

function fieldFromLoc(loc: unknown): string {
  if (!Array.isArray(loc) || loc.length === 0) {
    return 'this field';
  }
  const parts = loc.filter((part) => part !== 'body' && part !== 'query' && part !== 'path');
  const key = String(parts[parts.length - 1] ?? loc[loc.length - 1]);
  return FIELD_LABELS[key] || key.replace(/_/g, ' ');
}

function formatValidationItem(item: unknown): string {
  if (typeof item === 'string') {
    return item;
  }
  if (!item || typeof item !== 'object') {
    return '';
  }

  const err = item as { loc?: unknown; msg?: string; type?: string; ctx?: { reason?: string } };
  const field = fieldFromLoc(err.loc);
  const errType = err.type || '';
  const msg = (err.msg || 'is invalid').trim();
  const reason = err.ctx?.reason;

  if (field === 'email' || errType.includes('email')) {
    const extra = reason ? ` ${reason}` : '';
    return `Please enter a valid email address (accounts use email, not a username).${extra}`;
  }
  if (field === 'age' || errType.includes('int')) {
    return 'Age must be a whole number between 18 and 120.';
  }
  if (field === 'password') {
    if (errType.includes('max') || msg.includes('72')) {
      return 'Password cannot be longer than 72 characters.';
    }
    if (errType.includes('min') || msg.includes('6')) {
      return 'Password must be at least 6 characters long.';
    }
    return `Password is invalid: ${msg}`;
  }
  if (field === 'name') {
    return 'Please enter your name.';
  }
  if (errType.includes('missing')) {
    return `${field.charAt(0).toUpperCase()}${field.slice(1)} is required.`;
  }
  return `${field.charAt(0).toUpperCase()}${field.slice(1)} ${msg.charAt(0).toLowerCase()}${msg.slice(1)}.`;
}

function formatDetail(detail: unknown): string | null {
  if (detail == null) {
    return null;
  }
  if (typeof detail === 'string') {
    return detail;
  }
  if (Array.isArray(detail)) {
    const parts = detail.map(formatValidationItem).filter(Boolean);
    return parts.length
      ? Array.from(new Set(parts)).join(' ')
      : 'Some of the information you entered is not valid. Please review the form and try again.';
  }
  if (typeof detail === 'object') {
    const asRecord = detail as { msg?: string; message?: string };
    if (asRecord.msg || asRecord.message) {
      return asRecord.msg || asRecord.message || null;
    }
  }
  return null;
}

function mapKnownDetail(detail: string): string {
  const lower = detail.toLowerCase();
  if (lower.includes('email already registered')) {
    return 'This email is already registered. Try logging in instead, or use a different email.';
  }
  if (lower.includes('incorrect email or password')) {
    return 'Invalid email or password. Check your credentials, or register if you do not have an account yet.';
  }
  if (lower.includes('password cannot be longer than 72')) {
    return 'Password is too long. Please use 72 characters or less.';
  }
  if (lower.includes('invalid email')) {
    return 'Please enter a valid email address. Accounts are created with email, not a username.';
  }
  if (lower.includes('age must be')) {
    return 'Age must be between 18 and 120 years.';
  }
  if (lower.includes('name is required')) {
    return 'Name is required.';
  }
  if (lower.includes('password is required')) {
    return 'Password is required.';
  }
  if (lower.includes('email is required')) {
    return 'Email is required.';
  }
  if (lower.includes('could not validate credentials')) {
    return 'Your session is invalid or has expired. Please log in again.';
  }
  if (lower.includes('not enough permissions')) {
    return 'Access denied. Your account does not have permission for this action.';
  }
  if (lower.includes('verify your email')) {
    return 'Verify your email to continue. Check your inbox for a verification link.';
  }
  if (lower.includes('already verified')) {
    return 'This email is already verified.';
  }
  if (lower.includes('this verification link is invalid')) {
    return 'This verification link is invalid or has expired. Request a new one from the app.';
  }
  if (lower.includes('free questions for today') || lower.includes('token limit')) {
    return 'You have reached today\'s Ask limit. It resets at midnight UTC.';
  }
  if (lower.includes('too much research context')) {
    return 'This question needs too much research context. Try a more specific question.';
  }
  if (lower.includes('image storage is not configured')) {
    return 'Photo uploads are not configured on the server yet. Contact support or try again later.';
  }
  return detail;
}

function messageForStatus(status: number): string {
  switch (status) {
    case 400:
      return 'The server could not process this request. Check the information you entered and try again.';
    case 401:
      return 'You need to be logged in to do that. Please sign in and try again.';
    case 403:
      return 'Access denied. Your account does not have permission for this action.';
    case 404:
      return 'That service or page was not found. The API URL may be misconfigured, or the endpoint may have moved.';
    case 408:
    case 504:
      return 'The request timed out. The server may still be waking up — wait a few seconds and try again.';
    case 409:
      return 'This conflicts with an existing record. If you are registering, that email may already be in use.';
    case 422:
      return 'Some of the information you entered is not valid. Check your name, email, age (18–120), and password (6–72 characters).';
    case 429:
      return 'Too many attempts. Please wait a minute and try again.';
    case 500:
    case 502:
      return 'The server hit an unexpected problem. Please wait a moment and try again. If this keeps happening, the API may be restarting.';
    case 503:
      return 'The API is temporarily unavailable or still starting up (common on free hosting). Wait about 30 seconds and try again.';
    default:
      return `Something went wrong (error ${status}). Please try again. If it keeps happening, wait a moment and retry.`;
  }
}

export function getUserErrorMessage(error: unknown, fallback = 'An unexpected error occurred. Please try again.'): string {
  if (error && typeof error === 'object' && 'userMessage' in error) {
    const userMessage = (error as { userMessage?: unknown }).userMessage;
    if (typeof userMessage === 'string' && userMessage.trim()) {
      return userMessage;
    }
  }
  return fallback;
}

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Let the runtime set multipart boundaries; default application/json breaks file uploads.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers;
      if (headers) {
        if (typeof (headers as { delete?: (name: string) => void }).delete === 'function') {
          (headers as { delete: (name: string) => void }).delete('Content-Type');
          (headers as { delete: (name: string) => void }).delete('content-type');
        } else {
          delete (headers as Record<string, unknown>)['Content-Type'];
          delete (headers as Record<string, unknown>)['content-type'];
        }
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and provide better error messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status as number | undefined;
    const requestUrl = String(error.config?.url || '');
    const isAuthAttempt = requestUrl.includes('/login') || requestUrl.includes('/register');

    // Expired sessions should return to login, but failed login/register must stay put
    // so the form can show the actual error.
    if (status === 401 && !isAuthAttempt) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }

    const detailMessage = formatDetail(error.response?.data?.detail);

    if (detailMessage) {
      error.userMessage = mapKnownDetail(detailMessage);
    } else if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      error.userMessage =
        'Unable to reach the server. Check your internet connection. If you are using the live site, the API on Render may still be waking up — wait about 30 seconds and try again.';
    } else if (error.code === 'ECONNABORTED' || (typeof error.message === 'string' && error.message.includes('timeout'))) {
      error.userMessage =
        'The request timed out. The server is taking longer than expected (common when the API is starting). Please try again in a moment.';
    } else if (status) {
      error.userMessage = messageForStatus(status);
    } else {
      error.userMessage =
        (typeof error.message === 'string' && error.message) ||
        'An unexpected error occurred. Please try again. If it continues, refresh the page.';
    }

    return Promise.reject(error);
  }
);

export interface User {
  id: number;
  name: string;
  age: number;
  email: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  is_subscribed: boolean;
  is_admin: boolean;
  email_verified: boolean;
  email_verified_at?: string;
  subscription_status?: string | null;
  current_period_end?: string | null;
  created_at: string;
  updated_at?: string;
}

export interface UserCreate {
  name: string;
  age: number;
  email: string;
  password: string;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface UserLogin {
  email: string;
  password: string;
}

export interface UserProfileUpdate {
  name: string;
  age: number;
  phone?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}

export interface Token {
  access_token: string;
  token_type: string;
}

export interface RAGCitation {
  type: 'rule' | 'chunk';
  id: number;
  title: string;
  source: string;
  source_url?: string;
  page_start?: number;
  page_end?: number;
}

export interface RAGAnswer {
  answer: string;
  citations: RAGCitation[];
  quota?: AskQuota;
}

export interface AskQuota {
  unlimited: boolean;
  questions_used: number;
  questions_limit?: number | null;
  questions_remaining?: number | null;
  tokens_used: number;
  tokens_limit?: number | null;
  tokens_remaining?: number | null;
  resets_at: string;
}

export function getErrorCode(error: unknown): string | undefined {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return undefined;
  }
  const detail = (error as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
  if (detail && typeof detail === 'object' && 'code' in detail) {
    const code = (detail as { code?: unknown }).code;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

export interface ResearchPaper {
  id: number;
  title: string;
  authors?: string;
  publication_year?: number;
  category: string;
  abstract?: string;
  source_url?: string;
  page_count: number;
  created_at: string;
}

export interface ResearchListResponse {
  items: ResearchPaper[];
  total: number;
  limit: number;
  offset: number;
}

export interface ResearchFilters {
  category?: string;
  q?: string;
  limit?: number;
  offset?: number;
}

export interface AdminOverview {
  user_count: number;
  verified_user_count: number;
  admin_count: number;
  rag_document_count: number;
  active_listing_count: number;
  open_order_count: number;
  ask_questions_today: number;
  ask_tokens_today: number;
}

export interface AdminUserRow {
  id: number;
  name: string;
  email: string;
  age: number;
  email_verified: boolean;
  is_admin: boolean;
  is_subscribed: boolean;
  subscription_status?: string | null;
  created_at: string;
}

export interface AdminUserListResponse {
  items: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
}

export interface AdminAskDayRow {
  usage_date: string;
  question_count: number;
  token_count: number;
  active_users: number;
}

export interface AdminAskAnalytics {
  questions_today: number;
  tokens_today: number;
  active_users_today: number;
  daily: AdminAskDayRow[];
}

export interface AdminLogEntry {
  timestamp: string;
  level: string;
  logger: string;
  message: string;
}

export interface AdminMarketplaceListingRow {
  id: number;
  title: string;
  status: string;
  price_cents: number;
  seller_id: number;
  seller_name: string;
  seller_email: string;
  created_at: string;
}

export interface AdminMarketplaceOrderRow {
  id: number;
  listing_title: string;
  status: string;
  price_cents: number;
  buyer_name: string;
  seller_name: string;
  created_at: string;
}

export interface AdminRagDocumentRow {
  id: number;
  title: string;
  category: string;
  source_filename: string;
  page_count: number;
  created_at: string;
}

export interface RagIngestResult {
  created: number;
  updated: number;
  skipped: number;
}

export const adminAPI = {
  overview: (): Promise<AdminOverview> =>
    api.get('/admin/overview').then((res) => res.data),

  listUsers: (params: { q?: string; limit?: number; offset?: number } = {}): Promise<AdminUserListResponse> =>
    api.get('/admin/users', { params }).then((res) => res.data),

  askAnalytics: (days = 14): Promise<AdminAskAnalytics> =>
    api.get('/admin/ask-analytics', { params: { days } }).then((res) => res.data),

  listListings: (params: { status?: string; limit?: number; offset?: number } = {}): Promise<{
    items: AdminMarketplaceListingRow[];
    total: number;
  }> =>
    api.get('/admin/marketplace/listings', { params }).then((res) => res.data),

  listOrders: (params: { limit?: number; offset?: number } = {}): Promise<{
    items: AdminMarketplaceOrderRow[];
    total: number;
  }> =>
    api.get('/admin/marketplace/orders', { params }).then((res) => res.data),

  listDocuments: (params: { limit?: number; offset?: number } = {}): Promise<{
    items: AdminRagDocumentRow[];
    total: number;
  }> =>
    api.get('/admin/documents', { params }).then((res) => res.data),

  deleteDocument: (documentId: number): Promise<void> =>
    api.delete(`/admin/documents/${documentId}`).then(() => undefined),

  logs: (limit = 200): Promise<{ items: AdminLogEntry[] }> =>
    api.get('/admin/logs', { params: { limit } }).then((res) => res.data),

  ingestRules: (rules: unknown[]): Promise<RagIngestResult> =>
    api.post('/admin/ingest/rules', { rules }).then((res) => res.data),

  ingestPdf: (file: File): Promise<RagIngestResult> => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/admin/ingest/pdf', form, { timeout: 120000 }).then((res) => res.data);
  },
};

export const authAPI = {
  register: (userData: UserCreate): Promise<Token> =>
    api.post('/register', { ...userData, email: normalizeEmail(userData.email) }).then(res => res.data),
  
  login: (credentials: UserLogin): Promise<Token> =>
    api
      .post('/login', { ...credentials, email: normalizeEmail(credentials.email) })
      .then(res => res.data),
  
  getMe: (): Promise<User> =>
    api.get('/me').then(res => res.data),

  updateProfile: (payload: UserProfileUpdate): Promise<User> =>
    api.patch('/me', payload).then(res => res.data),

  verifyEmail: (token: string): Promise<{ email: string; email_verified: boolean }> =>
    api.post('/verify-email', { token }).then(res => res.data),

  resendVerification: (): Promise<{ message: string }> =>
    api.post('/resend-verification').then(res => res.data),
  
  getNewsletterSubscribers: (): Promise<User[]> =>
    api.get('/newsletter').then(res => res.data),
};

export const ragAPI = {
  ask: (question: string): Promise<RAGAnswer> =>
    api.post('/rag/ask', { question }, { timeout: 60000 }).then(res => res.data),

  quota: (): Promise<AskQuota> =>
    api.get('/rag/quota').then(res => res.data),
};

export const researchAPI = {
  list: (filters: ResearchFilters = {}): Promise<ResearchListResponse> =>
    api.get('/research', { params: filters }).then(res => res.data),

  categories: (): Promise<string[]> =>
    api.get('/research/categories').then(res => res.data),

  get: (id: number): Promise<ResearchPaper> =>
    api.get(`/research/${id}`).then(res => res.data),
};

export type ThreadCategory = 'discussion' | 'meetup' | 'research';
export type InterestStatus = 'pending' | 'contacted' | 'enrolled' | 'declined';

export interface AuthorSummary {
  id: number;
  name: string;
}

export interface CommunityThreadSummary {
  id: number;
  title: string;
  body: string;
  category: ThreadCategory;
  author: AuthorSummary;
  post_count: number;
  created_at: string;
  updated_at?: string;
}

export interface CommunityPost {
  id: number;
  thread_id: number;
  author: AuthorSummary;
  body: string;
  created_at: string;
  updated_at?: string;
}

export interface ThreadDetail {
  id: number;
  title: string;
  body: string;
  category: ThreadCategory;
  author: AuthorSummary;
  posts: CommunityPost[];
  created_at: string;
  updated_at?: string;
}

export interface ThreadListResponse {
  items: CommunityThreadSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface ThreadCreate {
  title: string;
  body: string;
  category?: ThreadCategory;
}

export interface ResearchInterest {
  id: number;
  user_id: number;
  topics: string;
  availability_notes?: string;
  consent_contact: boolean;
  status: InterestStatus;
  created_at: string;
  updated_at?: string;
  user_name?: string;
  user_email?: string;
}

export interface ResearchInterestCreate {
  topics: string;
  availability_notes?: string;
  consent_contact: boolean;
}

export interface CommunityThreadFilters {
  category?: string;
  limit?: number;
  offset?: number;
}

export const communityAPI = {
  listThreads: (filters: CommunityThreadFilters = {}): Promise<ThreadListResponse> =>
    api.get('/community/threads', { params: filters }).then((res) => res.data),

  createThread: (payload: ThreadCreate): Promise<ThreadDetail> =>
    api.post('/community/threads', payload).then((res) => res.data),

  getThread: (id: number): Promise<ThreadDetail> =>
    api.get(`/community/threads/${id}`).then((res) => res.data),

  reply: (threadId: number, body: string): Promise<CommunityPost> =>
    api.post(`/community/threads/${threadId}/posts`, { body }).then((res) => res.data),

  deleteThread: (id: number): Promise<void> =>
    api.delete(`/community/threads/${id}`).then(() => undefined),

  deletePost: (id: number): Promise<void> =>
    api.delete(`/community/posts/${id}`).then(() => undefined),

  getResearchInterest: (): Promise<ResearchInterest | null> =>
    api.get('/community/research-interest').then((res) => res.data),

  submitResearchInterest: (payload: ResearchInterestCreate): Promise<ResearchInterest> =>
    api.post('/community/research-interest', payload).then((res) => res.data),

  listResearchInterests: (): Promise<ResearchInterest[]> =>
    api.get('/community/research-interest/all').then((res) => res.data),
};

export type ListingStatus = 'draft' | 'active' | 'reserved' | 'sold' | 'cancelled';
export type OrderStatus =
  | 'pending_payment'
  | 'escrowed'
  | 'shipped'
  | 'confirmed'
  | 'burned'
  | 'expired';

export interface SellerSummary {
  id: number;
  name: string;
}

export interface ListingImage {
  id: number;
  url: string;
  sort_order: number;
  created_at: string;
}

export interface MarketplaceListingSummary {
  id: number;
  title: string;
  description: string;
  price_cents: number;
  status: ListingStatus;
  seller: SellerSummary;
  cover_image_url?: string | null;
  image_count: number;
  created_at: string;
  updated_at?: string;
}

export interface MarketplaceListingDetail {
  id: number;
  title: string;
  description: string;
  price_cents: number;
  status: ListingStatus;
  seller: SellerSummary;
  images: ListingImage[];
  created_at: string;
  updated_at?: string;
}

export interface MarketplaceListingListResponse {
  items: MarketplaceListingSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface CartItem {
  id: number;
  listing: MarketplaceListingSummary;
  created_at: string;
}

export interface CartResponse {
  items: CartItem[];
  total_cents: number;
}

export interface MarketplaceOrder {
  id: number;
  listing_id: number;
  listing_title: string;
  cover_image_url?: string | null;
  buyer_id: number;
  seller_id: number;
  buyer_name: string;
  seller_name: string;
  price_cents: number;
  status: OrderStatus;
  escrow_tx_hash?: string | null;
  resolve_tx_hash?: string | null;
  created_at: string;
  updated_at?: string;
  shipped_at?: string | null;
  resolved_at?: string | null;
}

export interface OrderListResponse {
  items: MarketplaceOrder[];
  total: number;
}

export function formatPriceCents(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
  }).format(cents / 100);
}

export function hasActiveSubscription(user: User | null | undefined): boolean {
  if (!user) {
    return false;
  }
  if (user.is_admin) {
    return true;
  }
  const status = normalizeSubscriptionStatus(user.subscription_status);
  return status === 'active' || status === 'trialing';
}

export interface CheckoutSessionResponse {
  session_id: string;
  checkout_url: string;
}

export interface BillingPlan {
  amount_cents: number;
  currency: string;
  interval: string;
  interval_count: number;
}

export interface PortalSessionResponse {
  session_id: string;
  portal_url: string;
}

export function formatBillingInterval(plan: BillingPlan): string {
  const count = plan.interval_count;
  const unit = plan.interval;
  if (count === 1) {
    return unit;
  }
  return `${count} ${unit}s`;
}

export const billingAPI = {
  getPlan: (): Promise<BillingPlan> =>
    api.get('/billing/plan').then((res) => res.data),

  createCheckoutSession: (
    successUrl: string,
    cancelUrl: string,
  ): Promise<CheckoutSessionResponse> =>
    api
      .post('/billing/create-checkout-session', {
        success_url: successUrl,
        cancel_url: cancelUrl,
      })
      .then((res) => res.data),

  createPortalSession: (returnUrl: string): Promise<PortalSessionResponse> =>
    api
      .post('/billing/create-portal-session', { return_url: returnUrl })
      .then((res) => res.data),
};

export const marketplaceAPI = {
  listListings: (params: { status?: string; limit?: number; offset?: number } = {}): Promise<MarketplaceListingListResponse> =>
    api.get('/marketplace/listings', { params }).then((res) => res.data),

  myListings: (params: { limit?: number; offset?: number } = {}): Promise<MarketplaceListingListResponse> =>
    api.get('/marketplace/mine', { params }).then((res) => res.data),

  getListing: (id: number): Promise<MarketplaceListingDetail> =>
    api.get(`/marketplace/listings/${id}`).then((res) => res.data),

  createListing: (formData: FormData): Promise<MarketplaceListingDetail> =>
    api
      .post('/marketplace/listings', formData, {
        transformRequest: (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete (headers as Record<string, unknown>)['Content-Type'];
            delete (headers as Record<string, unknown>)['content-type'];
          }
          return data;
        },
      })
      .then((res) => res.data),

  addImages: (id: number, formData: FormData): Promise<MarketplaceListingDetail> =>
    api
      .post(`/marketplace/listings/${id}/images`, formData, {
        transformRequest: (data, headers) => {
          if (headers && typeof headers === 'object') {
            delete (headers as Record<string, unknown>)['Content-Type'];
            delete (headers as Record<string, unknown>)['content-type'];
          }
          return data;
        },
      })
      .then((res) => res.data),

  updateListing: (
    id: number,
    payload: Partial<{ title: string; description: string; price_cents: number; status: ListingStatus }>
  ): Promise<MarketplaceListingDetail> =>
    api.patch(`/marketplace/listings/${id}`, payload).then((res) => res.data),

  deleteListing: (id: number): Promise<void> =>
    api.delete(`/marketplace/listings/${id}`).then(() => undefined),

  getCart: (): Promise<CartResponse> =>
    api.get('/marketplace/cart').then((res) => res.data),

  addToCart: (listingId: number): Promise<CartItem> =>
    api.post('/marketplace/cart', { listing_id: listingId }).then((res) => res.data),

  removeFromCart: (cartItemId: number): Promise<void> =>
    api.delete(`/marketplace/cart/${cartItemId}`).then(() => undefined),

  checkout: (): Promise<OrderListResponse> =>
    api.post('/marketplace/checkout').then((res) => res.data),

  listOrders: (role: 'all' | 'buyer' | 'seller' = 'all'): Promise<OrderListResponse> =>
    api.get('/marketplace/orders', { params: { role } }).then((res) => res.data),

  getOrder: (id: number): Promise<MarketplaceOrder> =>
    api.get(`/marketplace/orders/${id}`).then((res) => res.data),

  recordEscrow: (id: number, escrowTxHash?: string): Promise<MarketplaceOrder> =>
    api.post(`/marketplace/orders/${id}/escrow`, { escrow_tx_hash: escrowTxHash || null }).then((res) => res.data),

  markShipped: (id: number): Promise<MarketplaceOrder> =>
    api.post(`/marketplace/orders/${id}/ship`).then((res) => res.data),

  confirmDelivery: (id: number): Promise<MarketplaceOrder> =>
    api.post(`/marketplace/orders/${id}/confirm`).then((res) => res.data),

  burnOrder: (id: number): Promise<MarketplaceOrder> =>
    api.post(`/marketplace/orders/${id}/burn`).then((res) => res.data),
};

export default api;
